/* eslint-disable react/no-unknown-property */
import React, { useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Sphere, Text} from '@react-three/drei';
import * as THREE from 'three';
import styled from 'styled-components';
// import { MarketData } from '../../utils/marketData';
import type { MarketData } from '../../types';

const GlobeContainer = styled.div`
  width: 100%;
  max-width: 600px;
  aspect-ratio: 1 / 1;
  height: 100%;
  max-height: 600px;
  position: relative;
  margin: 0 auto;
  overflow: visible;
`;

const MarketTooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: absolute;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  background-color: white;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-size: 0.875rem;
  transform: translate(-50%, -100%);
  pointer-events: none;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.2s ease;
  z-index: 10;
`;

const MarketName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const MarketValue = styled.div<{ status: 'up' | 'down' | 'closed' }>`
  color: ${(props) => {
    switch (props.status) {
      case 'up':
        return '#00C853';
      case 'down':
        return '#FF3D00';
      default:
        return '#757575';
    }
  }};
`;

const MarketStatus = styled.div`
  font-size: 0.75rem;
  color: #757575;
`;

interface GlobeProps {
  markets: MarketData[];
}

const latLongToVector3 = (
  lat: number,
  long: number,
  radius: number
): THREE.Vector3 => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (long + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);

  return new THREE.Vector3(x, y, z);
};

function MarketPoints({
  markets,
  radius,
  onHover,
}: {
  markets: MarketData[];
  radius: number;
  onHover: (market: MarketData | null, x: number, y: number) => void;
}) {
  const pointsRef = React.useRef<THREE.Group>(null);

  return (
    <group ref={pointsRef}>
      {markets.map((market) => {
        const [long, lat] = market.coordinates;
        const position = latLongToVector3(lat, long, radius);
        const labelOffset = 0.03; // Distance above the point for the label on the z
        const labelPositionRadial = position.clone().normalize().multiplyScalar(radius + labelOffset);

        let color;
        switch (market.status) {
          case 'up':
            color = '#00C853';
            break;
          case 'down':
            color = '#FF3D00';
            break;
          default:
            color = '#757575';
        }

        // Calculate the normal direction (from globe center to market point)
        const normal = position.clone().normalize();

        // Find a vector in the tangent plane perpendicular to the normal
        const referenceVector = new THREE.Vector3(0, 1, 0);
        const tangent = referenceVector.clone().sub(
          normal.clone().multiplyScalar(normal.dot(referenceVector))
        ).normalize();

        // If the tangent vector is zero (normal is parallel to y-axis, i.e., at poles), use a default
        if (tangent.length() < 1e-6) {
          tangent.set(1, 0, 0); // Default to x-axis at the poles
        }

        // Rotate the tangent vector by 90 degrees around the normal to make the label horizontal
        tangent.applyAxisAngle(normal, -(Math.PI / 2));

        // The tangent vector is the "right" direction (local x-axis of the box)
        const right = tangent;

        // Compute the "up" direction (local y-axis) as perpendicular to normal and right
        const up = new THREE.Vector3().crossVectors(normal, right).normalize();

        // Create a rotation matrix to orient the label
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(right, up, normal); // right, up, forward
        const quaternion = new THREE.Quaternion().setFromRotationMatrix(matrix);

        // Offset the label position along the latitude (in the tangent plane)
        const latitudeOffset = 0.07;
        const labelPosition = labelPositionRadial.clone().add(up.clone().multiplyScalar(latitudeOffset));

        // Offset the text slightly above the box along the normal direction
        const textOffset = 0.006; // Small offset to place text above the box surface
        const textPosition = labelPosition.clone().add(normal.clone().multiplyScalar(textOffset));

        // Dynamically calculate box dimensions based on text length
        const fontSize = 0.03;
        const averageCharWidth = fontSize * 0.6; // Approximate average character width (adjust based on font)
        const textWidth = Math.min(market.name.length * averageCharWidth, 0.28); // Respect maxWidth of 0.28
        const paddingX = 0.02; // Padding on left and right
        const paddingY = 0.01; // Padding on top and bottom
        const boxWidth = textWidth + paddingX * 2; // Add padding on both sides
        const boxHeight = fontSize + paddingY * 2; // Add padding on top and bottom
        const boxDepth = 0.01; // Keep depth as is

        const handlePointerOver = (e: ThreeEvent<PointerEvent>) => {
          e.stopPropagation();
          e.stopPropagation();
          const vector = new THREE.Vector3(position.x, position.y, position.z);
          vector.project(e.camera);

          const x = (vector.x * 0.5 + 0.5) * window.innerWidth / 3;
          const y = (vector.y * -0.5 + 0.5) * window.innerHeight;

          onHover(market, x, y);
        };

        const handlePointerOut = () => {
          onHover(null, 0, 0);
        };

        return (
          <group key={market.id}>
            {/* Market Point with Hover Margin */}
            <group>
              {/* Visible Sphere */}
              <Sphere
                position={[position.x, position.y, position.z]}
                args={[0.015, 16, 16]}
              >
                <meshBasicMaterial color={color} />
              </Sphere>

              {/* Invisible Larger Sphere for Hover Margin */}
              <Sphere
                position={[position.x, position.y, position.z]}
                args={[0.015, 16, 16]} // Larger radius for hover margin (0.03 vs 0.015)
                visible={false} // Invisible but still interactive
                onPointerOver={handlePointerOver}
                onPointerOut={handlePointerOut}
              />
            </group>

            {/* Label Background (Rectangle) with Hover */}
            <mesh
              position={[labelPosition.x, labelPosition.y, labelPosition.z]}
              quaternion={quaternion}
              onPointerOver={handlePointerOver}
              onPointerOut={handlePointerOut}
            >
              <boxGeometry args={[boxWidth, boxHeight, boxDepth]} />
              <meshBasicMaterial
                color={
                  {
                    up: '#00C853',
                    down: '#FF3D00',
                    closed: '#757575',
                  }[market.status] || '#757575'
                }
                opacity={0.8}
                transparent
              />
            </mesh>

            {/* 3D Text Label */}
            <Text
              position={[textPosition.x, textPosition.y, textPosition.z]}
              fontSize={fontSize}
              color={
                {
                  up: '#05632c',   // Even darker version of rgb(5, 99, 44)
                  down: '#830909', // Even darker version of rgb(131, 9, 9)
                  closed: '#4A4A4A', // Even darker version of #757575
                }[market.status] || '#4A4A4A'
              }
              fontWeight={600}
              anchorX="center"
              anchorY="middle"
              maxWidth={0.28}
              textAlign="center"
              quaternion={quaternion}
            >
              {market.name}
            </Text>
          </group>
        );
      })}
    </group>
  );
}

function RotatingGlobe({
  markets,
  radius,
  onHover,
}: {
  markets: MarketData[];
  radius: number;
  onHover: (market: MarketData | null, x: number, y: number) => void;
}) {
  const groupRef = React.useRef<THREE.Group>(null);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotation.y += 0.001;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial
          color="#ffffff"
          wireframe
          transparent
          opacity={0.6}
        />
      </mesh>
      <MarketPoints markets={markets} radius={radius} onHover={onHover} />
    </group>
  );
}

function Globe({ markets }: GlobeProps) {
  const [hoveredMarket, setHoveredMarket] = React.useState<MarketData | null>(
    null
  );
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });

  // one-time touch detection
  const isTouchDevice =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const cameraPosition = useMemo<[number, number, number]>(() => {
    if (!isTouchDevice) return [0, 0, 2.5];
    const r = 2.5;
    const phi = Math.PI / 3.5; // ≈51.4°
    return [0, r * Math.cos(phi), r * Math.sin(phi)];
  }, [isTouchDevice]);

  const handleMarketHover = (
    market: MarketData | null,
    x: number,
    y: number
  ) => {
    setHoveredMarket(market);
    setTooltipPos({ x, y });
  };

  return (
    <GlobeContainer>
      <Canvas camera={{ position: cameraPosition, fov: 50 }}
      style={{ touchAction: isTouchDevice ? "pan-y" : "none" }}>
        <ambientLight intensity={0.5} />
        <RotatingGlobe
          markets={markets}
          radius={1}
          onHover={handleMarketHover}
        />
        {!isTouchDevice && (
        <OrbitControls
          enableZoom={false}
          enablePan={false}
          rotateSpeed={0.5}
          autoRotate={false}
          minPolarAngle={Math.PI / 3.5}
          maxPolarAngle={Math.PI / 3.5}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
          }}
        />
        )}
      </Canvas>

      <MarketTooltip
        visible={hoveredMarket !== null}
        x={tooltipPos.x}
        y={tooltipPos.y}
      >
        {hoveredMarket && (
          <>
            <MarketName>{hoveredMarket.name}</MarketName>
            <MarketValue status={hoveredMarket.status}>
              {hoveredMarket.value.toFixed(2)}
              {hoveredMarket.change > 0 ? '+' : ''}
              {hoveredMarket.changePercent.toFixed(2)}%
            </MarketValue>
            <MarketStatus>
              {hoveredMarket.status === 'closed'
                ? 'Market Closed'
                : `Trading Hours: ${hoveredMarket.openTime} - ${hoveredMarket.closeTime}`}
            </MarketStatus>
          </>
        )}
      </MarketTooltip>
    </GlobeContainer>
  );
}

export default Globe;